import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { BarList, EmptyState, KpiTile, Spinner, Table, Td, Th } from '@/components';
import { VillageArtLabel } from '@/components/VillageArtLabel';
import { formatAdminDateTime, formatDurationSeconds, formatInt, formatNumber } from '@/lib/format';
import { apiJson } from '@/lib/api';
import { villageSpriteForKey } from '@/lib/village-analytics-art';
import {
  avatarLabel,
  itemKindLabel,
  plotStatusLabel,
  rodLabel,
  tutorialLabel,
  villageActivityLabel,
} from '@/lib/village-activity-labels';

type CountRow = { key: string; count: number };
type ItemRow = { id: string; count: number };
type FishRow = { id: string; count: number; largest_weight: number };
type Plot = {
  index: number;
  crop_id: string | null;
  stage: number;
  status: string;
  stage_ready_at: string | null;
};
type VillageActivity = {
  has_save: boolean;
  has_sessions: boolean;
  save: {
    updated_at: string;
    schema_version: number;
    generation: number;
    app_version: string | null;
    last_installation_id: string | null;
    cleared: boolean;
  } | null;
  status: {
    source: 'save' | 'latest_session';
    coins: number;
    level: number;
    total_xp: number;
    xp_by_source: CountRow[];
    area_id: string | null;
    player_x: number | null;
    player_y: number | null;
    avatar: string | null;
    rod_level: number;
    has_jukebox: boolean;
    jukebox_track_id: string | null;
    on_bike: boolean;
    bike_area_id: string | null;
    on_boat: boolean;
    tutorial_step: number;
    seen_intro: boolean;
    read_dad_letter: boolean;
    seen_completion: boolean;
    first_village_day: string | null;
    daily_login_bonus_day: string | null;
    word_count: number;
    words: string[];
    quests_completed: string[];
    quests_active: string[];
    quests_found: string[];
    met_npc_ids: string[];
    total_harvests: number;
    total_catches: number;
    total_forages: number;
    total_mines: number;
    harvest_quiz: { word_id: string; ask_count: number; last_correct: boolean | null } | null;
    items: ItemRow[];
    fish: FishRow[];
    plots: Plot[];
    farm: {
      plot_count: number;
      planted: number;
      ready: number;
      growing: number;
      dry: number;
      wilted: number;
      tilled_empty: number;
      untilled: number;
    };
    forage: { ready: number; waiting: number; spots: number } | null;
    daily_fish: {
      day: string | null;
      objectives: { id: string; progress: number; completed: boolean }[];
      species_caught: string[];
    };
    claimed_free_levels: number[];
    claimed_premium_levels: number[];
  } | null;
  aggregates: {
    sessions: number;
    play_seconds: number;
    first_played_at: string | null;
    last_played_at: string | null;
    peak_score: number;
    peak_coins: number;
    peak_level: number;
    platforms: string[];
    devices: number;
  };
  timeline: {
    run_id: number | null;
    played_at: string;
    duration_seconds: number | null;
    score: number;
    platform: string | null;
    nickname: string | null;
    app_version: string | null;
    coins: number;
    level: number;
    words: number;
    quests: number;
    harvests: number;
    catches: number;
    forages: number;
    mines: number;
    coins_delta: number;
    harvests_delta: number;
    catches_delta: number;
    words_delta: number;
    quests_delta: number;
  }[];
  timeline_truncated: boolean;
};

const PLOT_TONE: Record<string, string> = {
  ready: 'border-emerald-200 bg-emerald-50',
  growing: 'border-sky-200 bg-sky-50',
  dry: 'border-amber-200 bg-amber-50',
  wilted: 'border-red-200 bg-red-50',
  tilled: 'border-stone-200 bg-stone-50',
  untilled: 'border-gray-200 bg-gray-50',
};

function formatDelta(n: number): string {
  if (n > 0) return `+${formatInt(n)}`;
  return formatInt(n);
}

function Thing({ id }: { id: string }) {
  return <VillageArtLabel sprite={villageSpriteForKey(id)}>{villageActivityLabel(id)}</VillageArtLabel>;
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-[22px] border border-gray-900/6 bg-white/82 p-5 shadow-[0_10px_18px_rgba(0,0,0,0.06)]">
      <h2 className="text-sm font-semibold text-gray-900">{title}</h2>
      {hint ? <p className="mt-1 text-sm text-gray-500">{hint}</p> : null}
      <div className="mt-4">{children}</div>
    </section>
  );
}

function Fact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <>
      <dt className="text-gray-500">{label}</dt>
      <dd>{value}</dd>
    </>
  );
}

function IdList({ ids, empty }: { ids: string[]; empty: string }) {
  if (ids.length === 0) return <p className="text-sm text-gray-500">{empty}</p>;
  return (
    <ul className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
      {ids.map((id) => (
        <li key={id} className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-800">
          <Thing id={id} />
        </li>
      ))}
    </ul>
  );
}

export function UserVillagePanel({ userId }: { userId: string }) {
  const query = useQuery({
    queryKey: ['admin', 'user', userId, 'village'],
    queryFn: () => apiJson<VillageActivity>(`/v1/admin/users/${encodeURIComponent(userId)}/village`),
  });

  if (query.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }
  if (query.error) {
    return (
      <p className="text-sm text-red-600">
        {(query.error as Error).message || 'Failed to load Hmong Village activity.'}
        <button type="button" className="ml-2 underline" onClick={() => void query.refetch()}>
          Retry
        </button>
      </p>
    );
  }
  const data = query.data;
  if (!data) return null;
  if (!data.has_save && !data.has_sessions && !data.save?.cleared) {
    return (
      <EmptyState
        title="No Hmong Village activity"
        hint="This account has no cloud save and no submitted Hmong Village sessions."
      />
    );
  }

  const status = data.status;
  const fromSave = status?.source === 'save';
  const bagGroups = groupItems(status?.items ?? []);

  return (
    <div className="space-y-5">
      {data.save?.cleared ? (
        <p className="text-sm text-amber-800">
          Cloud save was cleared {formatAdminDateTime(data.save.updated_at)}. Farm, bag, and fish
          log below are unavailable. Session history is kept.
        </p>
      ) : null}
      {status?.source === 'latest_session' && !data.save?.cleared ? (
        <p className="text-sm text-gray-600">
          No cloud save for this account. Coins, level, and lifetime counters are from the latest
          submitted session. Farm plots, the bag, and the fish log are only stored on the save.
        </p>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <KpiTile
          label="Coins"
          value={formatInt(status?.coins ?? 0)}
          tooltip={`Peak balance seen on a session or the save: ${formatInt(data.aggregates.peak_coins)}`}
        />
        <KpiTile
          label="Level"
          value={formatInt(status?.level ?? 0)}
          tooltip={`Peak level ${formatInt(data.aggregates.peak_level)} · ${formatInt(status?.total_xp ?? 0)} XP on the save`}
        />
        <KpiTile
          label="Sessions"
          value={formatInt(data.aggregates.sessions)}
          tooltip="Hmong Village runs submitted when the player left with a new high score."
        />
        <KpiTile
          label="Play time"
          value={formatDurationSeconds(data.aggregates.play_seconds)}
          tooltip="Sum of those submitted session lengths."
        />
        <KpiTile label="Harvests" value={formatInt(status?.total_harvests ?? 0)} />
        <KpiTile label="Fish caught" value={formatInt(status?.total_catches ?? 0)} />
      </div>

      <Section title={fromSave ? 'Current game' : 'Sessions'}>
        <dl className="grid gap-2 text-sm sm:grid-cols-2">
          {fromSave && status ? (
            <>
              <Fact label="Area" value={status.area_id ? villageActivityLabel(status.area_id) : '—'} />
              <Fact
                label="Position"
                value={
                  status.player_x != null && status.player_y != null
                    ? `${status.player_x}, ${status.player_y}`
                    : '—'
                }
              />
              <Fact label="Avatar" value={avatarLabel(status.avatar)} />
              <Fact label="Fishing rod" value={rodLabel(status.rod_level)} />
              <Fact
                label="Jukebox"
                value={
                  status.has_jukebox
                    ? status.jukebox_track_id
                      ? `Owned · ${villageActivityLabel(status.jukebox_track_id)}`
                      : 'Owned'
                    : 'Not purchased'
                }
              />
              <Fact
                label="Bike"
                value={
                  status.on_bike
                    ? `Riding${status.bike_area_id ? ` in ${villageActivityLabel(status.bike_area_id)}` : ''}`
                    : status.bike_area_id
                      ? `Parked in ${villageActivityLabel(status.bike_area_id)}`
                      : '—'
                }
              />
              <Fact label="Boat" value={status.on_boat ? 'On the boat' : 'Ashore'} />
              <Fact label="Tutorial" value={tutorialLabel(status.tutorial_step)} />
              <Fact label="Dad's letter" value={status.read_dad_letter ? 'Read' : 'Unread'} />
              <Fact label="Intro seen" value={status.seen_intro ? 'Yes' : 'No'} />
              <Fact label="All quests celebrated" value={status.seen_completion ? 'Yes' : 'No'} />
              <Fact label="First village day" value={status.first_village_day ?? '—'} />
              <Fact label="Last login bonus" value={status.daily_login_bonus_day ?? '—'} />
              <Fact label="Words discovered" value={formatInt(status.word_count)} />
              <Fact label="Quests completed" value={formatInt(status.quests_completed.length)} />
              <Fact label="Forages" value={formatInt(status.total_forages)} />
              <Fact label="Mine pulls" value={formatInt(status.total_mines)} />
              <Fact
                label="Forage spots"
                value={
                  status.forage
                    ? `${formatInt(status.forage.ready)} ready · ${formatInt(status.forage.waiting)} regrowing`
                    : '—'
                }
              />
              <Fact
                label="Cloud save"
                value={
                  data.save && !data.save.cleared
                    ? `${formatAdminDateTime(data.save.updated_at)}${data.save.app_version ? ` · v${data.save.app_version}` : ''}`
                    : '—'
                }
              />
              <Fact
                label="Level rewards claimed"
                value={`Free ${levelList(status.claimed_free_levels)} · Premium ${levelList(status.claimed_premium_levels)}`}
              />
            </>
          ) : (
            <>
              <Fact label="Words (latest session)" value={formatInt(status?.word_count ?? 0)} />
              <Fact label="Forages (latest session)" value={formatInt(status?.total_forages ?? 0)} />
              <Fact label="Mine pulls (latest session)" value={formatInt(status?.total_mines ?? 0)} />
            </>
          )}
          <Fact label="First session" value={formatAdminDateTime(data.aggregates.first_played_at)} />
          <Fact label="Latest session" value={formatAdminDateTime(data.aggregates.last_played_at)} />
          <Fact label="Peak score" value={formatInt(data.aggregates.peak_score)} />
          <Fact
            label="Devices"
            value={`${formatInt(data.aggregates.devices)}${data.aggregates.platforms.length ? ` · ${data.aggregates.platforms.join(', ')}` : ''}`}
          />
        </dl>
      </Section>

      {fromSave && status ? (
        <Section
          title="Farm"
          hint={`${formatInt(status.farm.planted)} planted · ${formatInt(status.farm.ready)} ready · ${formatInt(status.farm.wilted)} wilted · ${formatInt(status.total_harvests)} lifetime harvests`}
        >
          {status.harvest_quiz ? (
            <p className="mb-3 text-sm text-gray-600">
              Harvest quiz: {villageActivityLabel(status.harvest_quiz.word_id)} · ask{' '}
              {formatInt(status.harvest_quiz.ask_count)}
              {status.harvest_quiz.last_correct == null
                ? ''
                : status.harvest_quiz.last_correct
                  ? ' · last answer correct'
                  : ' · last answer missed'}
            </p>
          ) : null}
          {status.plots.length === 0 ? (
            <p className="text-sm text-gray-500">No farm plots on this save.</p>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
              {status.plots.map((plot) => (
                <div
                  key={plot.index}
                  className={`rounded-xl border px-3 py-2 text-sm ${PLOT_TONE[plot.status] ?? PLOT_TONE.untilled}`}
                >
                  <div className="font-medium">
                    {plot.crop_id ? <Thing id={plot.crop_id} /> : 'Empty'}
                  </div>
                  <p className="mt-1 text-xs text-gray-600">
                    Plot {plot.index + 1} · {plotStatusLabel(plot.status)}
                    {plot.crop_id ? ` · stage ${plot.stage}` : ''}
                  </p>
                  {plot.stage_ready_at ? (
                    <p className="text-xs text-gray-500">
                      Next stage {formatAdminDateTime(plot.stage_ready_at)}
                    </p>
                  ) : null}
                </div>
              ))}
            </div>
          )}
        </Section>
      ) : null}

      {fromSave && status ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Section title="Fish log" hint={`${formatInt(status.total_catches)} lifetime catches`}>
            {status.fish.length === 0 ? (
              <p className="text-sm text-gray-500">No species in the fish log.</p>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Species</Th>
                    <Th>Caught</Th>
                    <Th>Largest</Th>
                  </tr>
                </thead>
                <tbody>
                  {status.fish.map((fish) => (
                    <tr key={fish.id}>
                      <Td>
                        <Thing id={fish.id} />
                      </Td>
                      <Td>{formatInt(fish.count)}</Td>
                      <Td>{formatNumber(fish.largest_weight, { maxFractionDigits: 2 })}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            {status.daily_fish.day || status.daily_fish.objectives.length > 0 ? (
              <div className="mt-4 text-sm">
                <p className="font-medium text-gray-700">
                  Daily objectives {status.daily_fish.day ?? ''}
                </p>
                {status.daily_fish.objectives.length === 0 ? (
                  <p className="mt-1 text-gray-500">None rolled.</p>
                ) : (
                  <ul className="mt-1 space-y-1 text-gray-700">
                    {status.daily_fish.objectives.map((row) => (
                      <li key={row.id}>
                        {villageActivityLabel(row.id)} · {formatInt(row.progress)}
                        {row.completed ? ' · done' : ''}
                      </li>
                    ))}
                  </ul>
                )}
                {status.daily_fish.species_caught.length > 0 ? (
                  <p className="mt-2 text-gray-500">
                    Species today:{' '}
                    {status.daily_fish.species_caught.map((id) => villageActivityLabel(id)).join(', ')}
                  </p>
                ) : null}
              </div>
            ) : null}
          </Section>

          <Section
            title="Bag and purchases"
            hint="Current bag plus owned unlocks. The save does not keep a shop receipt, so seeds still here have not been planted, and sold items are gone."
          >
            <dl className="mb-4 grid gap-2 text-sm sm:grid-cols-2">
              <Fact label="Rod" value={rodLabel(status.rod_level)} />
              <Fact label="Jukebox" value={status.has_jukebox ? 'Purchased' : 'Not purchased'} />
            </dl>
            {bagGroups.length === 0 ? (
              <p className="text-sm text-gray-500">Bag is empty.</p>
            ) : (
              <div className="space-y-4">
                {bagGroups.map((group) => (
                  <div key={group.kind}>
                    <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-gray-500">
                      {group.kind}
                    </h3>
                    <Table>
                      <thead>
                        <tr>
                          <Th>Item</Th>
                          <Th>Count</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.items.map((item) => (
                          <tr key={item.id}>
                            <Td>
                              <Thing id={item.id} />
                            </Td>
                            <Td>{formatInt(item.count)}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      ) : null}

      {fromSave && status ? (
        <div className="grid gap-5 lg:grid-cols-2">
          <Section title="Quests">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              Completed
            </h3>
            <IdList ids={status.quests_completed} empty="None completed." />
            <h3 className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
              In progress
            </h3>
            <IdList ids={status.quests_active} empty="None underway." />
            <h3 className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
              Ready to turn in
            </h3>
            <IdList ids={status.quests_found} empty="None waiting on an NPC." />
            <h3 className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
              NPCs met
            </h3>
            <IdList ids={status.met_npc_ids} empty="None yet." />
          </Section>
          <Section title="Words and XP">
            <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
              XP by source
            </h3>
            <BarList
              preserveOrder
              emptyLabel="No XP on this save."
              data={status.xp_by_source.map((row) => ({
                key: row.key,
                count: row.count,
                label: villageActivityLabel(row.key),
              }))}
            />
            <h3 className="mb-2 mt-4 text-xs font-medium uppercase tracking-wide text-gray-500">
              Discovered words
            </h3>
            <IdList ids={status.words} empty="None discovered." />
          </Section>
        </div>
      ) : null}

      <Section
        title="Timeline"
        hint="Each row is a session submitted when the player left with a new high score. The small numbers are the change since the previous submitted session. Visits that did not raise the score are not stored."
      >
        {data.timeline.length === 0 ? (
          <p className="text-sm text-gray-500">No submitted sessions.</p>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Played</Th>
                  <Th>Length</Th>
                  <Th>Score</Th>
                  <Th>Coins</Th>
                  <Th>Lv</Th>
                  <Th>Words</Th>
                  <Th>Quests</Th>
                  <Th>Harvests</Th>
                  <Th>Fish</Th>
                  <Th>Forage</Th>
                  <Th>Mine</Th>
                </tr>
              </thead>
              <tbody>
                {data.timeline.map((row) => (
                  <tr key={`${row.run_id ?? 'run'}-${row.played_at}`}>
                    <Td className="whitespace-nowrap">
                      <div>{formatAdminDateTime(row.played_at)}</div>
                      <div className="text-xs text-gray-500">
                        {[row.platform, row.nickname, row.app_version ? `v${row.app_version}` : null]
                          .filter(Boolean)
                          .join(' · ') || '—'}
                      </div>
                    </Td>
                    <Td className="whitespace-nowrap">{formatDurationSeconds(row.duration_seconds)}</Td>
                    <Td>{formatInt(row.score)}</Td>
                    <Td>
                      <CountWithDelta value={row.coins} delta={row.coins_delta} />
                    </Td>
                    <Td>{formatInt(row.level)}</Td>
                    <Td>
                      <CountWithDelta value={row.words} delta={row.words_delta} />
                    </Td>
                    <Td>
                      <CountWithDelta value={row.quests} delta={row.quests_delta} />
                    </Td>
                    <Td>
                      <CountWithDelta value={row.harvests} delta={row.harvests_delta} />
                    </Td>
                    <Td>
                      <CountWithDelta value={row.catches} delta={row.catches_delta} />
                    </Td>
                    <Td>{formatInt(row.forages)}</Td>
                    <Td>{formatInt(row.mines)}</Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {data.timeline_truncated ? (
          <p className="mt-2 text-xs text-gray-500">Showing the 100 most recent sessions.</p>
        ) : null}
      </Section>
    </div>
  );
}

function CountWithDelta({ value, delta }: { value: number; delta: number }) {
  return (
    <div>
      <div>{formatInt(value)}</div>
      <div className="text-xs text-gray-500">{formatDelta(delta)}</div>
    </div>
  );
}

function levelList(levels: number[]): string {
  if (levels.length === 0) return 'none';
  return levels.join(', ');
}

function groupItems(items: ItemRow[]): { kind: string; items: ItemRow[] }[] {
  const order = ['Seeds', 'Produce', 'Fish', 'Forage', 'Mined', 'Other'];
  const groups = new Map<string, ItemRow[]>();
  for (const item of items) {
    const kind = itemKindLabel(item.id);
    const bucket = groups.get(kind) ?? [];
    bucket.push(item);
    groups.set(kind, bucket);
  }
  return order
    .filter((kind) => groups.has(kind))
    .map((kind) => ({ kind, items: groups.get(kind) ?? [] }));
}
